import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as DSOIcon,
  Assessment as OverdueIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { KPIResponse } from '@/shared/api';

interface KPIDashboardProps {
  data: KPIResponse | null;
  loading: boolean;
  onKPIClick?: (kpiType: string, filters: any) => void;
}

const KPIDashboard: React.FC<KPIDashboardProps> = ({
  data,
  loading,
  onKPIClick,
}) => {
  const calculateTrend = (current: number, previous: number | undefined): string => {
    if (previous === undefined || previous === 0) return '';
    
    const change = ((current - previous) / previous) * 100;
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  const metrics = data?.current;
  const previousMetrics = data?.previous;

  // Debug logs
  console.log('🔍 KPIDashboard Debug:', {
    data,
    metrics,
    previousMetrics,
    loading
  });
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  const formatTrend = (trend: string) => {
    const isPositive = trend.startsWith('+');
    const isNegative = trend.startsWith('-');
    
    return {
      value: trend,
      color: isPositive ? 'success.main' : isNegative ? 'error.main' : 'text.secondary',
      icon: isPositive ? TrendingUpIcon : isNegative ? TrendingDownIcon : null,
    };
  };

  const KPICard = ({ 
    title, 
    value, 
    trend, 
    icon: Icon, 
    tooltip, 
    onClick 
  }: {
    title: string;
    value: string | number;
    trend?: string;
    icon: any;
    tooltip?: string;
    onClick?: () => void;
  }) => {
    const trendInfo = trend ? formatTrend(trend) : null;

    return (
      <Paper 
        sx={{ 
          p: 3, 
          height: '100%',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s',
          '&:hover': onClick ? {
            boxShadow: 3,
            transform: 'translateY(-2px)',
          } : {},
        }}
        onClick={onClick}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography variant="subtitle2" fontWeight="600" color="text.primary">
              {title}
            </Typography>
            {tooltip && (
              <Tooltip title={tooltip}>
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>
          {typeof value === 'number' && title.includes('Monto') ? formatCurrency(value) : value}
        </Typography>

        {trendInfo && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {trendInfo.icon && (
              <trendInfo.icon sx={{ fontSize: 16, color: trendInfo.color }} />
            )}
            <Typography variant="body2" sx={{ color: trendInfo.color, fontWeight: 500 }}>
              {trendInfo.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              vs. período anterior
            </Typography>
          </Box>
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Paper>
    );
  };

  const RecoveryRatesCard = () => (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <TrendingUpIcon sx={{ color: 'success.main', fontSize: 24 }} />
        <Typography variant="subtitle2" fontWeight="600">
          % Recuperado
        </Typography>
        <Tooltip title="Porcentaje de facturas recuperadas en cada período">
          <IconButton size="small">
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" fontWeight="500">30 días</Typography>
            <Typography variant="h6" fontWeight="600">{metrics?.recovery_rate_30.toFixed(1)}%</Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={metrics?.recovery_rate_30 || 0} 
            sx={{ height: 8, borderRadius: 4 }}
            color="success"
          />
          <Typography variant="caption" color="text.secondary">
            Tendencia: {formatTrend(calculateTrend(metrics?.recovery_rate_30 || 0, previousMetrics?.recovery_rate_30)).value}
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" fontWeight="500">60 días</Typography>
            <Typography variant="h6" fontWeight="600">{metrics?.recovery_rate_60.toFixed(1)}%</Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={metrics?.recovery_rate_60 || 0} 
            sx={{ height: 8, borderRadius: 4 }}
            color="warning"
          />
          <Typography variant="caption" color="text.secondary">
            Tendencia: {formatTrend(calculateTrend(metrics?.recovery_rate_60 || 0, previousMetrics?.recovery_rate_60)).value}
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" fontWeight="500">90 días</Typography>
            <Typography variant="h6" fontWeight="600">{metrics?.recovery_rate_90.toFixed(1)}%</Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={metrics?.recovery_rate_90 || 0} 
            sx={{ height: 8, borderRadius: 4 }}
            color="error"
          />
          <Typography variant="caption" color="text.secondary">
            Tendencia: {formatTrend(calculateTrend(metrics?.recovery_rate_90 || 0, previousMetrics?.recovery_rate_90)).value}
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mt: 2 }} />}
    </Paper>
  );

  const OverdueInvoicesCard = () => (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <OverdueIcon sx={{ color: 'error.main', fontSize: 24 }} />
        <Typography variant="subtitle2" fontWeight="600">
          Facturas en Mora por Tramo
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Object.entries(metrics?.overdue_invoices_by_period || {}).map(([period, count]) => (
          <Box 
            key={period}
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: onKPIClick ? 'pointer' : 'default',
              p: 1,
              borderRadius: 1,
              '&:hover': onKPIClick ? {
                bgcolor: 'grey.50',
              } : {},
            }}
            onClick={onKPIClick ? () => onKPIClick('overdue', { period }) : undefined}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={period} 
                size="small" 
                variant="outlined"
                color={
                  period === '0-30' ? 'success' :
                  period === '31-60' ? 'warning' :
                  period === '61-90' ? 'error' : 'default'
                }
              />
              <Typography variant="body2" color="text.secondary">días</Typography>
            </Box>
            <Typography variant="h6" fontWeight="600">
              {count}
            </Typography>
          </Box>
        ))}
      </Box>

      {loading && <LinearProgress sx={{ mt: 2 }} />}
    </Paper>
  );

  if (loading && !metrics) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
          gap: 3 
        }}>
          {[1, 2, 3, 4].map((i) => (
            <Paper sx={{ p: 3, height: 140 }} key={i}>
              <LinearProgress />
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Primary KPIs */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
        gap: 3 
      }}>
        <KPICard
          title="Monto Pendiente"
          value={metrics?.pending_amount || 0}
          trend={calculateTrend(metrics?.pending_amount || 0, previousMetrics?.pending_amount)}
          icon={MoneyIcon}
          tooltip="Total de dinero pendiente de cobro"
          onClick={onKPIClick ? () => onKPIClick('pending_amount', {}) : undefined}
        />

        <KPICard
          title="Días Promedio de Cobro"
          value={`${metrics?.dias_promedio_cobro || 0} días`}
          trend={calculateTrend(metrics?.dias_promedio_cobro || 0, previousMetrics?.dias_promedio_cobro)}
          icon={DSOIcon}
          tooltip="Promedio de días que toma cobrar una factura"
          onClick={onKPIClick ? () => onKPIClick('dias_promedio_cobro', {}) : undefined}
        />

        <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1', md: '3 / -1' } }}>
          <RecoveryRatesCard />
        </Box>
      </Box>

      {/* Secondary KPIs */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 3 
      }}>
        <OverdueInvoicesCard />
      </Box>
    </Box>
  );
};

export { KPIDashboard };
